"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _ServiceRegistry = _interopRequireDefault(require("../util/ServiceRegistry"));
var _LoggingService = require("./LoggingService");
var _Events = require("../constants/Events");
var _AppUtil = require("../util/AppUtil");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2020 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
class PerformanceService {
  constructor() {
    this._appStartTime = new Date().getTime();
    this._isEventEmitted = false;
    this._fileSize = -1;
    this._appReadyToRender = -1;
    this._actionReceivedOn = -1;
    this._appInitializeReceivedOn = -1;
    this._fileDownloadCompletedOn = -1;
    this._errorDuringLinearizationFlow = false;
    this._rangesResolveTimes = [];
  }
  initialize(action, timeInfo) {
    this._actionReceivedOn = timeInfo.actionReceivedOn;
    this._hostAppStartTime = timeInfo.hostAppStartTime;
    this._appStartTime = timeInfo.appStartTime;
    this._appInitializeReceivedOn = timeInfo.appInitializeReceivedOn;
    if (action !== "preview") {
      this._isEventEmitted = true;
    }
    this._appStore = _ServiceRegistry.default.getService("AppStore");
  }
  setPreviewDropinReadyToRender() {
    this._previewDropinReadyToRender = new Date().getTime();
    const time = Math.ceil(this._previewDropinReadyToRender - this._appStartTime);
    _ServiceRegistry.default.getService("LoggingService").logExternal("File preview ready to render.", time);
  }
  setAppReadyToRender() {
    this._appReadyToRender = new Date().getTime();
  }
  setFileDownloadCompletedOn() {
    this._fileDownloadCompletedOn = new Date().getTime();
  }
  setCompleteFileLoadedOn() {
    this._completeFileLoadedOn = new Date().getTime();
    this._logCompleteFileLoaded();
    if (this._previewLoaded && !this._isEventEmitted) {
      this._isEventEmitted = true;
      const eventData = _objectSpread(_objectSpread({}, (0, _AppUtil.getBrowserOSDeviceInfo)()), this.getPerformanceEventLog());
      this.emitPerformanceObject(_LoggingService.LoggingConstants.APP_PERFORMANCE, eventData);
    }
  }
  setErrorDuringLinearizationFlow() {
    this._errorDuringLinearizationFlow = true;
  }
  setLinearizationInfoProvided(value) {
    this._linearizationInfoProvided = value;
  }
  setInitialBufferResolveTime(value) {
    this._initialBufferResolveTime = value;
  }
  setGetInfoResolveTime(value) {
    this._getInfoResolveTime = value;
  }
  setRangesResolveTime(value) {
    this._rangesResolveTimes.push(value);
  }
  setPreviewDialogOpened() {
    // If dialog is opened instead of file preview, ignore this log
    // Will happen in case of non pdf data bytes or password protected files
    this._isEventEmitted = true;
  }
  _shouldSetPreviewLoadedCondition() {
    return this._appStore.appRenderingBlocked;
  }
  setPreviewLoaded() {
    if (this._shouldSetPreviewLoadedCondition()) {
      this._isEventEmitted = true;
      return;
    }
    const previewConfig = this._appStore.configStore.getActionConfig("preview");
    if (!this._isEventEmitted) {
      this._previewLoaded = new Date().getTime();
      this._logPreviewLoaded();
      if (!previewConfig.enableLinearization || this._completeFileLoadedOn) {
        this._isEventEmitted = true;
        const eventData = _objectSpread(_objectSpread({}, (0, _AppUtil.getBrowserOSDeviceInfo)()), this.getPerformanceEventLog());
        this.emitPerformanceObject(_LoggingService.LoggingConstants.APP_PERFORMANCE, eventData);
      }
    }
  }
  setFileSize(fileSize) {
    this._fileSize = fileSize;
  }
  _logPreviewLoaded() {
    const time = Math.ceil(this._previewLoaded - this._appStartTime);
    _ServiceRegistry.default.getService("LoggingService").logExternal("File preview loaded.", time);
  }
  _logCompleteFileLoaded() {
    const time = Math.ceil(this._completeFileLoadedOn - this._appStartTime);
    _ServiceRegistry.default.getService("LoggingService").logExternal("PDF viewer ready.", time);
  }
  emitPerformanceObject(logMessage, logObject) {
    _ServiceRegistry.default.getService("LoggingService").logExternal(logMessage, logObject);
    _ServiceRegistry.default.getService("EventsService").sendEvent(_Events.InternalEvents.APP_PERF_DATA, logObject);
  }
  getPerformanceEventLog() {
    return {
      hostAppStartTime: this._hostAppStartTime,
      appInitializeReceivedOn: this._appInitializeReceivedOn,
      appStartTime: this._appStartTime,
      appReadyToRender: this._appReadyToRender,
      previewDropinReadyToRender: this._previewDropinReadyToRender,
      fileDownloadCompletedOn: this._fileDownloadCompletedOn,
      previewRequestReceived: this._actionReceivedOn,
      previewLoaded: this._previewLoaded,
      fileSize: this._fileSize,
      preset: this._appStore.configStore.getActionConfig("preview").embedMode,
      isOnDevice: this._appStore.isOnDevice,
      integrationName: this._appStore.configStore.getIntegrationName(),
      isLinearizationEnabled: this._appStore.configStore.getActionConfig("preview").enableLinearization,
      linearizationInfo: this._appStore.configStore.getActionConfig("preview").enableLinearization ? {
        completeFileLoadedOn: this._completeFileLoadedOn,
        errorDuringLinearizationFlow: this._errorDuringLinearizationFlow,
        fileDownloadTimeBreakup: {
          initialBufferResolveTime: this._initialBufferResolveTime,
          getInfoResolveTime: this._getInfoResolveTime,
          rangesResolveTime: this._rangesResolveTimes
        },
        makeMultipartRangeCalls: !!this._appStore.configStore.getActionConfig("preview").linearizationConfig.makeMultipartRangeCalls,
        initiateEarlyFileDownload: !!this._appStore.configStore.getActionConfig("preview").linearizationConfig.initiateEarlyFileDownload,
        linearizationInfoProvided: this._linearizationInfoProvided
      } : {}
    };
  }
}
exports.default = PerformanceService;